import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Bot, Phone, Calendar, Target, Clock, Zap,
  CheckCircle, Star, MessageSquare, DollarSign, TrendingUp,
  Shield, Users, Smartphone, Mail, Globe, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';

const SmallBusinessPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Heart className="w-4 h-4" />
              Built for Small Business
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              AI Automation for Small Businesses & Solo Professionals
            </h1>
            <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto mb-8">
              Enterprise-level AI assistant at small business prices. No technical expertise required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login?signup=true">
                <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50 px-8">
                  Start Free - No Credit Card
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Affordability Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Big Business Technology, Small Business Price
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Why should only big companies afford 24/7 customer service? We made AI accessible to everyone.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-6 text-center">
                <DollarSign className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">Starts at $49/mo</h3>
                <p className="text-slate-600">
                  Less than a part-time employee for just a few hours. Get 24/7 coverage instead.
                </p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-6 text-center">
                <Zap className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">5-Minute Setup</h3>
                <p className="text-slate-600">
                  No developers needed. No IT department required. Just answer a few questions and you're live.
                </p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-6 text-center">
                <TrendingUp className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">Grow With You</h3>
                <p className="text-slate-600">
                  Start small, scale up. Our plans grow with your business without expensive migrations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* No Technical Skills Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Zero Technical Skills Required
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                If you can fill out a form, you can set up your AI assistant. We've made the complex simple.
              </p>
              <ul className="space-y-4">
                {[
                  'Answer questions about your business - we build your AI',
                  'Copy-paste one line of code to your website (or skip it entirely)',
                  'Connect WhatsApp, email, or social media with one click',
                  'Customize responses in plain English - no coding ever'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Setup in 3 Steps</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold flex-shrink-0">1</div>
                  <div>
                    <p className="font-medium text-slate-900">Tell us about your business</p>
                    <p className="text-sm text-slate-600">What you do, your hours, common questions</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold flex-shrink-0">2</div>
                  <div>
                    <p className="font-medium text-slate-900">Choose your channels</p>
                    <p className="text-sm text-slate-600">Website, WhatsApp, email, or all three</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold flex-shrink-0">3</div>
                  <div>
                    <p className="font-medium text-slate-900">Go live!</p>
                    <p className="text-sm text-slate-600">Your AI starts answering immediately</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Multi-Channel Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Works Where Your Customers Are
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              One AI assistant across all your communication channels. Customers get consistent service everywhere.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Globe, name: 'Website', desc: 'Chat widget on your site' },
              { icon: Smartphone, name: 'WhatsApp', desc: 'Most popular messaging app' },
              { icon: Mail, name: 'Email', desc: 'Auto-respond to inquiries' },
              { icon: MessageSquare, name: 'Social Media', desc: 'Facebook & Instagram DMs' }
            ].map((channel) => (
              <Card key={channel.name} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <channel.icon className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-900 mb-1">{channel.name}</h3>
                  <p className="text-sm text-slate-600">{channel.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Perfect For Solo Pros & Small Teams
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Freelancers', desc: 'Handle client inquiries while you focus on work' },
              { title: 'Local Services', desc: 'Plumbers, electricians, cleaners - never miss a job' },
              { title: 'Consultants', desc: 'Qualify leads and book calls automatically' },
              { title: 'Retail Shops', desc: 'Answer questions about hours, stock, and services' },
              { title: 'Clinics', desc: 'Schedule appointments and answer patient FAQs' },
              { title: 'Restaurants', desc: 'Take reservations and answer menu questions' }
            ].map((useCase) => (
              <Card key={useCase.title} className="border-slate-200 bg-white hover:border-emerald-300 transition-colors">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">{useCase.title}</h3>
                  <p className="text-slate-600 text-sm">{useCase.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-emerald-500 to-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Your Business Deserves 24/7 Support Too
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Start free today. Upgrade when you're ready.
          </p>
          <Link to="/login?signup=true">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50 px-8">
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default SmallBusinessPage;
